import { useCallback, useEffect, useState } from 'react'

import {
  CompetenciesCompetenciesFinderSuggestionsDocument,
  CompetenciesFinderFactorialCompetencyFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'

interface BaseCompetencySuggestionTarget {
  id: number | string
  title: string
  description: string
}

interface JobCatalogTreeRoleTarget extends BaseCompetencySuggestionTarget {
  type: 'jobcatalog_treerole'
  context?: JobCatalogTreeRoleContext
}
interface JobCatalogTreeRoleContext {
  // Examples: role_siblings, levels underneath, parent nodes
}

export type CompetencySuggestionTarget = JobCatalogTreeRoleTarget

export const canSuggestForTarget = (target: CompetencySuggestionTarget) => {
  return target.title.trim().length > 0 && target.description.trim().length > 0
}

const SUGGESTIONS_PER_PAGE = 6
const MAX_PAGE = 2

type UseSuggestionsParams = {
  target: CompetencySuggestionTarget
  mock?: boolean
  initialUuids: string[]
}

type UseSuggestionsReturn = {
  suggestions: CompetenciesFinderFactorialCompetencyFragment[]
  error: boolean
  loading: boolean
  loadingMore: boolean
  loadingQuantity: number
  canSuggestMore: boolean
  fetchMoreSuggestions: (excludeUuids: string[]) => Promise<void>
}

export const useFactorialCompetenciesSuggestions = ({
  target,
  initialUuids,
}: UseSuggestionsParams): UseSuggestionsReturn => {
  const [initialUuidsSnapshot] = useState(() => (initialUuids || []).sort().join(','))

  const { company } = useCurrentSession()
  const isTargetValid = canSuggestForTarget(target)
  const { title, description } = target
  const [
    fetchInitialSuggestions,
    { loading: initialLoading, error: initialError, data: initialData },
  ] = useMutation(CompetenciesCompetenciesFinderSuggestionsDocument, {
    extractorFn: (data) =>
      data.competenciesMutations.suggestFromJobCatalogCompetenciesFactorialCompetency,
  })

  useEffect(() => {
    async function loadInitial() {
      await fetchInitialSuggestions({
        variables: {
          companyId: company.id,
          excludeUuids: initialUuidsSnapshot ? initialUuidsSnapshot.split(',') : [],
          nodeTitle: title,
          nodeDescription: description,
        },
      })
    }
    if (isTargetValid) {
      loadInitial()
    }
  }, [isTargetValid, fetchInitialSuggestions, title, description, company.id, initialUuidsSnapshot])

  const initialSuggestions = (
    initialData?.competenciesMutations?.suggestFromJobCatalogCompetenciesFactorialCompetency
      ?.factorialCompetencies || []
  ).slice(0, SUGGESTIONS_PER_PAGE)

  const [fetchMore, { loading: moreLoading, error: moreError, data: moreData }] = useMutation(
    CompetenciesCompetenciesFinderSuggestionsDocument,
    {
      extractorFn: (data) =>
        data.competenciesMutations.suggestFromJobCatalogCompetenciesFactorialCompetency,
    }
  )

  const moreSuggestions = (
    moreData?.competenciesMutations?.suggestFromJobCatalogCompetenciesFactorialCompetency
      ?.factorialCompetencies || []
  ).slice(0, SUGGESTIONS_PER_PAGE)

  const suggestions = [...initialSuggestions, ...moreSuggestions]

  const canSuggestMore =
    suggestions.length > 0 &&
    suggestions.length < SUGGESTIONS_PER_PAGE * MAX_PAGE &&
    !initialLoading &&
    !moreLoading &&
    isTargetValid

  const fetchMoreSuggestions = useCallback(
    async (exclude: string[]) => {
      await fetchMore({
        variables: {
          companyId: company.id,
          excludeUuids: exclude ?? [],
          nodeTitle: title,
          nodeDescription: description,
        },
      })
    },
    [fetchMore, company.id, title, description]
  )

  return {
    suggestions,
    error: !!(initialError || moreError),
    loading: initialLoading,
    loadingMore: moreLoading,
    canSuggestMore,
    fetchMoreSuggestions,
    loadingQuantity: SUGGESTIONS_PER_PAGE,
  }
}
